import React, { useState } from 'react';
import { Installer } from './components/Installer';
import { ProductsPage } from './components/ProductsPage';
import { CustomersPage } from './components/CustomersPage';
import { Activity, Package, Users, Globe } from 'lucide-react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

type Tab = 'events' | 'products' | 'customers';

const TabContent: React.FC = () => {
  const { t, dir } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('events');

  const tabs = [
    { id: 'events' as Tab, label: t.eventTester, icon: Activity },
    { id: 'products' as Tab, label: t.products, icon: Package },
    { id: 'customers' as Tab, label: t.customers, icon: Users },
  ];

  return (
    <div className="min-h-screen flex flex-col" dir={dir}>
      {/* Navigation - Neobrutalist Style */}
      <header className="bg-neo-yellow border-4 border-neo-black sticky top-0 z-10 shadow-neo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-neo-black border-4 border-neo-black flex items-center justify-center shadow-neo-sm">
                <Activity className="w-6 h-6 text-neo-white" />
              </div>
              <span className="font-bold text-xl tracking-tight text-neo-black font-mono">{t.appName}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden md:block bg-neo-white border-4 border-neo-black px-3 py-1 font-mono text-sm font-bold shadow-neo-sm">
                {t.version}
              </span>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-neo-white border-b-4 border-neo-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-2 py-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 font-mono text-sm font-bold border-4 border-neo-black shadow-neo-sm transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-neo-blue translate-y-1 shadow-neo'
                      : 'bg-neo-white hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12 w-full">
        {activeTab === 'events' && (
          <div className="mb-8">
            <div className="bg-neo-blue border-4 border-neo-black p-6 shadow-neo">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-neo-black border-4 border-neo-black flex items-center justify-center shadow-neo-sm">
                  <Activity className="w-8 h-8 text-neo-blue" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-neo-black font-mono uppercase tracking-tight">{t.liveEventTester}</h1>
                  <p className="text-neo-black font-mono mt-2 text-sm md:text-base">{t.liveEventTesterDesc}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="mb-8">
            <div className="bg-neo-blue border-4 border-neo-black p-6 shadow-neo">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-neo-black border-4 border-neo-black flex items-center justify-center shadow-neo-sm">
                  <Package className="w-8 h-8 text-neo-blue" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-neo-black font-mono uppercase tracking-tight">{t.productsPageTitle}</h1>
                  <p className="text-neo-black font-mono mt-2 text-sm md:text-base">{t.productsPageDesc}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="mb-8">
            <div className="bg-neo-blue border-4 border-neo-black p-6 shadow-neo">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-neo-black border-4 border-neo-black flex items-center justify-center shadow-neo-sm">
                  <Users className="w-8 h-8 text-neo-blue" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-neo-black font-mono uppercase tracking-tight">{t.customersPageTitle}</h1>
                  <p className="text-neo-black font-mono mt-2 text-sm md:text-base">{t.customersPageDesc}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Page Content */}
        {activeTab === 'events' && <Installer />}
        {activeTab === 'products' && <ProductsPage />}
        {activeTab === 'customers' && <CustomersPage />}
      </main>

      <footer className="bg-neo-black border-t-4 border-neo-black py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-neo-white font-mono text-sm">
            &copy; {new Date().getFullYear()} {t.copyright}
          </span>
        </div>
      </footer>
    </div>
  );
};

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage } = useLanguage();

  return (
    <button
      onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
      className="bg-neo-white border-4 border-neo-black px-3 py-1 font-mono text-sm font-bold shadow-neo-sm flex items-center gap-2 hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
      title={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
    >
      <Globe className="w-4 h-4" />
      {language === 'en' ? 'AR' : 'EN'}
    </button>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <TabContent />
    </LanguageProvider>
  );
};

export default App;
